import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import * as XLSX from "xlsx-js-style";

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function oneMonthAgoYMD() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatDateForExcel(v) {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

export default function CrushRecyclingReport() {
  const [fromDate, setFromDate] = useState(oneMonthAgoYMD());
  const [toDate, setToDate] = useState(todayYMD());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const pageGroupSize = 20;

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.recyclingWeight += num(r.recyclingWeight);
        acc.crush1Hours += num(r.crush1Hours);
        acc.crush2Hours += num(r.crush2Hours);
        acc.recyclingHours += num(r.recyclingHours);
        acc.totalHours += num(r.totalHours);
        return acc;
      },
      {
        recyclingWeight: 0,
        crush1Hours: 0,
        crush2Hours: 0,
        recyclingHours: 0,
        totalHours: 0,
      }
    );
  }, [rows]);

  const totalPages = Math.ceil(rows.length / pageSize);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage]);

  const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }

  async function loadReport() {
    try {
      if (!fromDate || !toDate) {
        return alert("Please select From Date and To Date");
      }

      setLoading(true);

      const res = await api.get(`/api/reports/crush-recycling-summary`, {
        params: {
          from: fromDate,
          to: toDate,
        },
      });

      setRows(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load report"
      );
      setRows([]);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
  }, []);

  function exportExcel() {
    if (!rows.length) {
      return alert("No data to export");
    }

    const fromText = formatDateForExcel(fromDate);
    const toText = formatDateForExcel(toDate);

    const sheetData = [
      ["Saudi Mais Co. For Mais Irrigation Systems"],
      ["Crush & Recycling Report"],
      ["From", fromText, "", "", "", "To Date:", toText],
      [],
      [
        "Sr",
        "Item Code",
        "Recycling Weight (Kg)",
        "Crush1 Hours",
        "Crush2 Hours",
        "Recycling Hours",
        "Total Hours",
      ],
      ...rows.map((r, i) => [
        i + 1,
        r.itemCode || "",
        num(r.recyclingWeight),
        num(r.crush1Hours),
        num(r.crush2Hours),
        num(r.recyclingHours),
        num(r.totalHours),
      ]),
      [
        "",
        "TOTAL",
        totals.recyclingWeight,
        totals.crush1Hours,
        totals.crush2Hours,
        totals.recyclingHours,
        totals.totalHours,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    ws["!cols"] = [
      { wch: 6 },
      { wch: 16 },
      { wch: 22 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
    ];

    ws["!rows"] = [
      { hpt: 22 },
      { hpt: 20 },
      { hpt: 20 },
      { hpt: 8 },
      { hpt: 24 },
    ];

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    ];

    const borderStyle = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    const noBorderStyle = {
      top: { style: "none" },
      bottom: { style: "none" },
      left: { style: "none" },
      right: { style: "none" },
    };

    const titleStyle = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
      border: noBorderStyle,
    };

    const subTitleStyle = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: "center", vertical: "center" },
      border: noBorderStyle,
    };

    const headerStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "D9D9D9" } },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: borderStyle,
    };

    const leftStyle = {
      alignment: { horizontal: "left", vertical: "center" },
      border: borderStyle,
    };

    const centerStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle,
    };

    const rightStyle = {
      alignment: { horizontal: "right", vertical: "center" },
      border: borderStyle,
    };

    const dateLeftStyle = {
      alignment: { horizontal: "left", vertical: "center" },
      border: noBorderStyle,
    };

    const dateRightStyle = {
      alignment: { horizontal: "right", vertical: "center" },
      border: noBorderStyle,
    };

    const totalTextStyle = {
      font: { bold: true },
      alignment: { horizontal: "left", vertical: "center" },
      border: borderStyle,
    };

    const totalNumberStyle = {
      font: { bold: true },
      alignment: { horizontal: "right", vertical: "center" },
      border: borderStyle,
    };

    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) ws[cellRef] = { t: "s", v: "" };

        ws[cellRef].s = {
          alignment: { vertical: "center" },
          border: noBorderStyle,
        };
      }
    }

    ws["A1"].s = titleStyle;
    ws["A2"].s = subTitleStyle;

    for (let C = 0; C <= 6; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: 2, c: C });
      ws[cellRef].s = dateLeftStyle;
    }

    ws["A3"].s = {
      ...dateLeftStyle,
      font: { bold: true },
    };

    ws["B3"].s = dateLeftStyle;

    ws["F3"].s = {
      ...dateRightStyle,
      font: { bold: true },
    };

    ws["G3"].s = dateLeftStyle;

    for (let C = 0; C <= 6; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: 4, c: C });
      ws[cellRef].s = headerStyle;
    }

    const dataStartRow = 5;
    const totalRow = dataStartRow + rows.length;

    for (let R = dataStartRow; R < totalRow; R++) {
      ws[XLSX.utils.encode_cell({ r: R, c: 0 })].s = centerStyle;
      ws[XLSX.utils.encode_cell({ r: R, c: 1 })].s = leftStyle;

      for (let C = 2; C <= 6; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        ws[cellRef].s = rightStyle;
        ws[cellRef].z = "0.####";
      }
    }

    ws[XLSX.utils.encode_cell({ r: totalRow, c: 0 })].s = totalTextStyle;
    ws[XLSX.utils.encode_cell({ r: totalRow, c: 1 })].s = totalTextStyle;

    for (let C = 2; C <= 6; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: totalRow, c: C });
      ws[cellRef].s = totalNumberStyle;
      ws[cellRef].z = "0.####";
    }

    ws["!pageSetup"] = {
      paperSize: 9,
      orientation: "landscape",
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      verticalCentered: false,
    };

    ws["!margins"] = {
      left: 0.25,
      right: 0.25,
      top: 0.4,
      bottom: 0.4,
      header: 0.2,
      footer: 0.2,
    };

    ws["!ref"] = `A1:G${totalRow + 1}`;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    XLSX.writeFile(
      wb,
      `Crush_Recycling_Report_${fromDate}_to_${toDate}.xlsx`
    );
  }

  return (
    <div className="container-fluid" style={{ padding: 16 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Crush & Recycling Report</h2>

            <button className="btn btn-success" onClick={exportExcel}>
              Export Excel
            </button>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3 d-grid">
                  <button className="btn btn-primary" onClick={loadReport}>
                    Search
                  </button>
                </div>

                <div className="col-md-3 text-md-end">
                  {loading ? <span className="text-muted">Loading...</span> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 70 }} className="text-center">
                        Sr
                      </th>
                      <th style={{ width: 160 }}>Item Code</th>
                      <th style={{ width: 180 }} className="text-end">
                        Recycling Weight
                      </th>
                      <th style={{ width: 150 }} className="text-end">
                        Crush1 Hours
                      </th>
                      <th style={{ width: 150 }} className="text-end">
                        Crush2 Hours
                      </th>
                      <th style={{ width: 170 }} className="text-end">
                        Recycling Hours
                      </th>
                      <th style={{ width: 150 }} className="text-end">
                        Total Hours
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {!paginatedRows.length ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted p-4">
                          No report data found
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((r, i) => (
                        <tr
                          key={`${r.itemCode}_${(currentPage - 1) * pageSize + i}`}
                        >
                          <td className="text-center">
                            {(currentPage - 1) * pageSize + i + 1}
                          </td>
                          <td>{r.itemCode}</td>

                          <td className="text-end">
                            {num(r.recyclingWeight).toFixed(2)}
                          </td>

                          <td className="text-end">
                            {num(r.crush1Hours).toFixed(2)}
                          </td>

                          <td className="text-end">
                            {num(r.crush2Hours).toFixed(2)}
                          </td>

                          <td className="text-end">
                            {num(r.recyclingHours).toFixed(2)}
                          </td>

                          <td className="text-end fw-semibold">
                            {num(r.totalHours).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}

                    {!!rows.length && (
                      <tr className="table-light fw-bold">
                        <td colSpan="2">Total</td>

                        <td className="text-end">
                          {totals.recyclingWeight.toFixed(2)}
                        </td>

                        <td className="text-end">
                          {totals.crush1Hours.toFixed(2)}
                        </td>

                        <td className="text-end">
                          {totals.crush2Hours.toFixed(2)}
                        </td>

                        <td className="text-end">
                          {totals.recyclingHours.toFixed(2)}
                        </td>

                        <td className="text-end">
                          {totals.totalHours.toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                  <div>
                    <b>Total: {rows.length}</b>
                  </div>

                  {totalPages > 0 && (
                    <div className="d-flex gap-1 flex-wrap justify-content-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(startPage - 1)}
                        disabled={startPage === 1}
                      >
                        &laquo;
                      </button>

                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const page = startPage + i;
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${
                              currentPage === page
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(endPage + 1)}
                        disabled={endPage >= totalPages}
                      >
                        &raquo;
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}